import { Link } from '@tanstack/react-router';
import { Bot, Check, ChevronDown, DoorOpen, X } from 'lucide-react';
import { useState, type ReactNode } from 'react';
import {
  CRITERION_MARK_LABELS,
  PIPELINE_PATH_STAGES,
  canRecordWithdrawal,
  type ApplicationStage,
  type CandidateApplicationRow,
  type CandidateProfile,
  type StageBarKey,
} from '@nest-recruit/shared';
import { Button } from '../../../../components/Button';
import { Popover, usePopover } from '../../../../components/Popover';
import { SkeletonLines } from '../../../../components/Skeleton';
import { Stars } from '../../../../components/Stars';
import { STAGE_TONES } from '../../../../components/status-tones';
import { ToneChip, toneClass } from '../../../../components/Tone';
import { ServerErrorState } from '../../../../components/system-states/SystemState';
import { errorMessage } from '../../../../lib/api-client';
import { formatDate, formatDateTime } from '../../../../lib/format';
import { useStageLabels } from '../../../../lib/modules';
import { useCanEdit } from '../../../../stores/session';
import { useToasts } from '../../../../stores/toasts';
import { usePipelineOptions } from '../../../pipeline/api/pipeline-api';
import { CONTACT_ATTEMPTS_CHECKED_BY_SERVER, intentFor, reasonOptions } from '../../../pipeline/move-intent';
import { ReasonPicker } from '../../../sorting/components/ReasonPicker';
import { useApplicationOnOpen, useRejectApplication, useUndoReject } from '../../api/candidate-panel-api';
import { ProfileBreakdownView } from '../../../ranking/components/ProfileBreakdownView';
import { StageChip } from '../CandidateBits';
import { WithdrawalPicker } from '../WithdrawalPicker';
import { applicationSalary, markOfMatch, pointsText, stepsOf, type Step } from './panel-format';
import styles from './CandidatePanel.module.css';

const SALARY_FIELD = 'expectedSalaryMinor';
/** Rejecting is checked against the whole path: the job's own switches only matter to forward moves. */
const WHOLE_PATH: ReadonlySet<ApplicationStage> = new Set(PIPELINE_PATH_STAGES);

/** Where on the job the next step is taken (§L33's stage screens). Selected has none. */
function nextScreenOf(row: CandidateApplicationRow): StageBarKey | null {
  if (row.browseStage === null || row.browseStage === 'selected' || row.browseStage === 'rejected') return null;
  if (row.browseStage === 'interview') return row.stage === 'interview_1' || row.stage === 'interview_2' || row.stage === 'interview_3' ? row.stage : null;
  return row.browseStage;
}

/**
 * Jobs applied (CandidatePanel.dc.html): one card per application — the job, the salary against
 * its budget, the stepper, the score — with Reject… and the next step for someone who may move it,
 * and the match score, answers and history on demand.
 */
export function JobsTab({ profile }: { readonly profile: CandidateProfile }): ReactNode {
  const count = profile.applications.length;
  if (count === 0) {
    return (
      <div className={styles.tabBody}>
        <div className={styles.empty}>
          <span className={styles.emptyTitle}>No applications</span>
          <span className={styles.emptyBody}>This person was added without a job.</span>
        </div>
      </div>
    );
  }
  return (
    <div className={styles.tabBody}>
      <span className={styles.tabHeading}>{count === 1 ? '1 job' : `${count} jobs`}</span>
      {profile.applications.map((row) =>
        row.canOpen ? <ApplicationCard key={row.id} row={row} profile={profile} /> : <OutsideCard key={row.id} row={row} />,
      )}
    </div>
  );
}

/** D1: an application outside the viewer's access stays listed with venture and stage only. */
function OutsideCard({ row }: { readonly row: CandidateApplicationRow }): ReactNode {
  return (
    <article className={`${toneClass} ${styles.appCard}`} data-tone={STAGE_TONES[row.stage]} data-outside>
      <div className={styles.appHead}>
        <span className={styles.appTitle}>Outside your access</span>
        <StageChip stage={row.stage} />
      </div>
      <span className={styles.sub}>{row.ventureName}</span>
    </article>
  );
}

function ApplicationCard({ row, profile }: { readonly row: CandidateApplicationRow; readonly profile: CandidateProfile }): ReactNode {
  const stageLabels = useStageLabels();
  const canMoveApplications = useCanEdit('application');
  const [isOpen, setIsOpen] = useState(false);
  const canWrite = profile.can.edit && canMoveApplications && row.isLive;
  const isSalaryHidden = profile.candidate.restrictedFields?.includes(SALARY_FIELD) ?? false;
  const salary = applicationSalary(row);
  const hasTest = profile.tests.some((test) => test.applicationId === row.id);
  const steps = stepsOf(row, stageLabels[row.stage], hasTest);
  const canReject = canWrite && intentFor(row.stage, 'rejected', WHOLE_PATH, false).kind === 'reject';
  const next = canWrite ? nextScreenOf(row) : null;
  const meta = [row.ventureName, row.departmentName].filter(Boolean).join(' · ');

  return (
    <article className={`${toneClass} ${styles.appCard}`} data-tone={STAGE_TONES[row.stage]}>
      <div className={styles.appHead}>
        {row.jobId ? (
          <Link to="/jobs/$jobId" params={{ jobId: row.jobId }} className={styles.appTitle}>
            {row.jobTitle ?? '—'}
          </Link>
        ) : (
          <span className={styles.appTitle}>{row.jobTitle ?? '—'}</span>
        )}
        <StageChip stage={row.stage} />
        <span className={styles.spacer} />
        <span className={styles.sub}>
          Applied <b className={styles.num}>{formatDate(row.appliedAt)}</b>
        </span>
      </div>
      <div className={styles.appMeta}>
        {row.reference && <span className={styles.mono}>{row.reference}</span>}
        {meta && <span>{meta}</span>}
        <span>Source: {row.sourceLabel ?? '—'}</span>
        <span>Recruiter: {row.recruiterName ?? '—'}</span>
      </div>
      <div className={styles.salaryBox} data-over={(!isSalaryHidden && salary.isOver) || undefined}>
        {isSalaryHidden ? (
          <span>
            Expects <b className={styles.num}>—</b>
          </span>
        ) : (
          <>
            <span>
              Expects <b className={styles.num}>{salary.expectsText}</b> a month
            </span>
            {salary.isOver && <ToneChip tone="urgent">{salary.overText}</ToneChip>}
            <span>{salary.note}</span>
          </>
        )}
      </div>
      <Stepper steps={steps} />
      <div className={styles.appFoot}>
        <span className={styles.scoreLine}>
          Match <b className={styles.scoreValue}>{row.matchScore ?? '—'}</b>
          <Stars value={row.stars} />
        </span>
        <span className={styles.scoreLine}>
          HR ★ <b className={styles.num}>{row.hrStars === null ? '—' : row.hrStars.toFixed(1)}</b>
        </span>
        {row.stepDetail && <span className={styles.sub}>{row.stepDetail}</span>}
        <span className={styles.spacer} />
        {canReject && <RejectButton row={row} candidateId={profile.candidate.id} candidateName={profile.candidate.fullName} />}
        {next && row.jobId && (
          <Link to="/jobs/$jobId/$tab" params={{ jobId: row.jobId, tab: next }} className={styles.nextStep}>
            Open {next === 'interview_1' || next === 'interview_2' || next === 'interview_3' ? 'the interview' : stageLabelOf(next)}
          </Link>
        )}
      </div>
      <button type="button" className={styles.more} aria-expanded={isOpen} onClick={() => setIsOpen(!isOpen)}>
        <ChevronDown size={14} aria-hidden className={styles.chevron} data-open={isOpen || undefined} />
        {isOpen ? 'Hide match score, answers and history' : 'Match score, answers and history'}
      </button>
      {isOpen && <ApplicationDetail row={row} candidateName={profile.candidate.fullName} canWrite={canWrite} />}
    </article>
  );
}

const SCREEN_LABELS: Readonly<Record<string, string>> = {
  ranking: 'Automated ranking',
  review: 'Review',
  shortlist: 'Shortlist',
  contact: 'Contact',
  assessment: 'Assessment',
};

function stageLabelOf(key: StageBarKey): string {
  return SCREEN_LABELS[key] ?? key;
}

function Stepper({ steps }: { readonly steps: readonly Step[] }): ReactNode {
  return (
    <ol className={styles.steps} aria-label="Stages">
      {steps.map((step, index) => (
        <li key={step.key} className={styles.step} data-state={step.state} aria-current={step.state === 'current' ? 'step' : undefined}>
          <span className={styles.stepDot} aria-hidden>
            {step.state === 'done' && <Check size={11} strokeWidth={3.5} />}
            {step.state === 'stop' && <X size={11} strokeWidth={3.5} />}
          </span>
          {step.label}
          {index < steps.length - 1 && <span className={styles.stepBar} data-done={step.state === 'done' || undefined} aria-hidden />}
        </li>
      ))}
    </ol>
  );
}

/** F8.13 from the card: the reason picker beside the button; the move is the pipeline's own. */
function RejectButton({ row, candidateId, candidateName }: { readonly row: CandidateApplicationRow; readonly candidateId: string; readonly candidateName: string }): ReactNode {
  const { isOpen, anchor, triggerRef, toggle, close } = usePopover();
  const options = usePipelineOptions(isOpen);
  const canPool = useCanEdit('talent_pool');
  const reject = useRejectApplication();
  const undo = useUndoReject();
  const show = useToasts((state) => state.show);
  const showUndo = useToasts((state) => state.showUndo);
  return (
    <>
      <Button ref={triggerRef} variant="danger" size="sm" onClick={toggle} aria-haspopup="dialog" aria-expanded={isOpen}>
        Reject…
      </Button>
      {isOpen && (
        <Popover anchor={anchor} onClose={close} align="right" label={`Reject ${candidateName}`}>
          {options.data ? (
            <ReasonPicker
              mode="reject"
              reasons={reasonOptions(options.data.reasons, [row.stage])}
              pools={options.data.pools}
              canPool={canPool}
              contactAttempts={CONTACT_ATTEMPTS_CHECKED_BY_SERVER}
              candidateName={candidateName}
              canAddToCvBank
              onConfirm={(choice) => {
                close();
                reject.mutate(
                  { candidateId, applicationId: row.id, jobId: row.jobId, reasonCode: choice.reasonCode, poolIds: choice.poolIds, newPoolNames: choice.newPoolNames, addToCvBank: choice.addToCvBank },
                  {
                    onSuccess: (result) =>
                      showUndo(`${candidateName} rejected for ${row.jobTitle ?? 'this job'}`, () =>
                        undo.mutate({ actionId: result.action.id, jobId: row.jobId }, { onError: (error) => show({ tone: 'danger', message: "Couldn't undo that", detail: errorMessage(error) }) }),
                      ),
                    onError: (error) => show({ tone: 'danger', message: `${candidateName} stayed where they were`, detail: errorMessage(error) }),
                  },
                );
              }}
            />
          ) : options.error ? (
            <p className={`${styles.quiet} ${styles.popoverNote}`}>The reasons didn't load. {errorMessage(options.error)}</p>
          ) : (
            <div className={styles.popoverBody}>
              <SkeletonLines count={4} />
            </div>
          )}
        </Popover>
      )}
    </>
  );
}

/** The application's detail, loaded when the card is opened: §L1's breakdown, the answers, the history. */
function ApplicationDetail({ row, candidateName, canWrite }: { readonly row: CandidateApplicationRow; readonly candidateName: string; readonly canWrite: boolean }): ReactNode {
  const detail = useApplicationOnOpen(row.id, true);
  if (detail.error && !detail.data) return <ServerErrorState onRetry={() => void detail.refetch()} />;
  if (!detail.data) {
    return (
      <div className={styles.detail}>
        <SkeletonLines count={6} />
      </div>
    );
  }
  const { application, history } = detail.data;
  return (
    <div className={styles.detail}>
      <section>
        <h4 className={styles.detailTitle}>
          Match <span className={styles.sub}>A sorting aid, never a decision</span>
        </h4>
        {application.profileBreakdown ? (
          <ProfileBreakdownView breakdown={application.profileBreakdown} />
        ) : application.matchScore === null || application.breakdown.length === 0 ? (
          <p className={styles.quiet}>Not ranked yet.</p>
        ) : (
          <div className={styles.matchTable} role="table" aria-label="Criteria breakdown">
            <div role="row" className={styles.matchRow}>
              <span role="columnheader">Criterion</span>
              <span role="columnheader">Evidence</span>
              <span role="columnheader">Match</span>
              <span role="columnheader">Points</span>
            </div>
            {application.breakdown.map((item) => {
              const mark = markOfMatch(item.match);
              return (
                <div role="row" key={item.criterionId} className={styles.matchRow}>
                  <span role="cell">{item.label}</span>
                  <span role="cell">{item.evidence}</span>
                  <span role="cell" className={styles.mark} data-mark={mark}>
                    {CRITERION_MARK_LABELS[mark]}
                  </span>
                  <span role="cell" className={styles.num}>
                    {pointsText(item.points, item.weight)}
                  </span>
                </div>
              );
            })}
          </div>
        )}
      </section>
      <section>
        <h4 className={styles.detailTitle}>
          Answers <span className={styles.sub}>As given on this application</span>
        </h4>
        {application.formAnswers.length === 0 ? (
          <p className={styles.quiet}>No answers on this application.</p>
        ) : (
          <dl className={styles.facts}>
            {application.formAnswers.map((answer) => (
              <div key={answer.label} className={styles.fact}>
                <dt>{answer.label}</dt>
                <dd>{answer.isRestricted ? <span title="Not visible to your role">—</span> : (answer.value ?? '—')}</dd>
              </div>
            ))}
          </dl>
        )}
      </section>
      <section>
        <h4 className={styles.detailTitle}>History</h4>
        <ol className={styles.history}>
          {history.map((entry) => (
            <li key={entry.id} className={styles.historyRow}>
              <span className={styles.historyWhen}>{formatDateTime(entry.occurredAt)}</span>
              <span className={styles.historyWhat}>
                {entry.summary}
                {!entry.isSystem && entry.actorName ? <span className={styles.quiet}> · {entry.actorName}</span> : null}
              </span>
              {entry.isSystem && (
                <ToneChip tone="ended">
                  <Bot size={12} aria-hidden /> Automatic
                </ToneChip>
              )}
            </li>
          ))}
        </ol>
      </section>
      <div className={styles.detailActions}>
        {canWrite && canRecordWithdrawal(application.stage) && <WithdrawButton applicationId={application.id} version={application.version} candidateName={candidateName} />}
        <Link to="/applications/$applicationId" params={{ applicationId: application.id }} className={styles.linkButton}>
          Open the application
        </Link>
      </div>
    </div>
  );
}

/** S29 Q4 — the picker opens beside the button (CLAUDE.md §5). */
function WithdrawButton({ applicationId, version, candidateName }: { readonly applicationId: string; readonly version: number; readonly candidateName: string }): ReactNode {
  const { anchor, triggerRef, toggle, close } = usePopover();
  return (
    <>
      <Button ref={triggerRef} variant="outline" size="sm" icon={DoorOpen} onClick={toggle} aria-haspopup="dialog">
        Record withdrawal
      </Button>
      <Popover anchor={anchor} onClose={close} label={`Record that ${candidateName} withdrew`}>
        <WithdrawalPicker applicationId={applicationId} version={version} candidateName={candidateName} onDone={close} />
      </Popover>
    </>
  );
}
